<script lang="ts">
import type { NormalizedTabsBlockItem, TabsBlockItem, TabsBlockItemValue } from '#layers/blocks/utils/tabs-block-items';
import type { TabsProps } from '#ui/types';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';

/** Props exposed by the Tabs block. */
interface TabsBlockProps {
	/** Tabs shown by the block. */
	items?: TabsProps<TabsBlockItem>['items'];
	/** Initially selected tab when the block is uncontrolled. */
	defaultValue?: TabsBlockItemValue;
	/** Accent color forwarded to DTabs. */
	color?: TabsProps<TabsBlockItem>['color'];
	/** Visual tab style. */
	variant?: TabsProps<TabsBlockItem>['variant'];
	/** Control size forwarded to DTabs. */
	size?: TabsProps<TabsBlockItem>['size'];
	/** Tab list orientation. */
	orientation?: TabsProps<TabsBlockItem>['orientation'];
	/** Collapses overflowing tab triggers into a dropdown. */
	collapsible?: boolean;
}

/** Model value forwarded to DTabs. */
type TabsBlockModelValue = Extract<TabsProps<TabsBlockItem>['modelValue'], TabsBlockItemValue>;

const __DsBlock = {
	type: 'container',
	name: '$t:blocks.tabs.name',
	description: '$t:blocks.tabs.description',
	icon: 'ic:baseline-tab',
	category: 'layout',
	tags: ['layout', 'form-container'],
	interactive: true,
	slots: ({ props }) => getTabsBlockSlots(props.items),
	onInserted({ getProps, setProps }) {
		const props = getProps();

		if (props.items?.length) {
			return;
		}

		setProps({
			...props,
			defaultValue: props.defaultValue ?? DEFAULT_TABS_BLOCK_VALUE,
			items: createDefaultTabsBlockItems(),
		});
	},
	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'items',
			label: '$t:blocks.tabs.props.items',
			block: BuiltinBlockIds.InputRepeater,
			props: {
				schema: [
					{
						name: 'label',
						label: '$t:common.label',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'value',
						label: '$t:common.value',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'slotId',
						hidden: true,
						default: createTabsBlockSlotId,
					},
					{
						name: 'icon',
						label: '$t:common.icon',
						block: BuiltinBlockIds.InputIcon,
						nullable: true,
					},
					{
						name: 'disabled',
						label: '$t:common.disabled',
						block: BuiltinBlockIds.InputSwitch,
					},
				],
				displayBlock: {
					id: BuiltinBlockIds.DisplayFormattedValue,
					valueKey: 'label',
				},
			},
		},
		{
			name: 'defaultValue',
			label: '$t:blocks.tabs.props.default_value',
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
			props: {
				label: '$t:common.appearance',
			},
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: ['pill', 'link'],
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'orientation',
			label: '$t:blocks.common.props.orientation',
			type: 'enum',
			values: ['horizontal', 'vertical'],
		},
		{
			name: 'collapsible',
			label: '$t:blocks.tabs.props.collapsible',
		},
	],
}</script>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import { blockExpressionMarker, defineBlock, FormSchemaKind, useBlockInEditorContext } from '#layers/block-engine/public';
import {
	createDefaultTabsBlockItems,
	createTabsBlockSlotId,
	DEFAULT_TABS_BLOCK_VALUE,
	getTabsBlockSlots,
	normalizeTabsBlockItems,
} from '#layers/blocks/utils/tabs-block-items';
import { useTranslationUtils } from '#layers/i18n/public';

const {
	collapsible = false,
	items = createDefaultTabsBlockItems(),
	orientation = 'horizontal',
	variant = 'pill',
	defaultValue,
} = defineProps<TabsBlockProps>();

const model = defineModel<TabsBlockModelValue>();
const { blockInEditor, getProps, getSlotNames, isPreview, removeSlotChildren, setProp } = useBlockInEditorContext<TabsBlockProps>();

;

const { translateString } = useTranslationUtils();

/** Removes serialized children whose tab slot no longer exists. */
function removeStaleSlotChildren(items: TabsBlockItem[]) {
	const currentSlotNames = new Set(getTabsBlockSlots(items).map((slot) => slot.name));

	for (const slotName of getSlotNames()) {
		if (!currentSlotNames.has(slotName)) {
			removeSlotChildren(slotName);
		}
	}
}

/** Returns whether the serialized tab items are supplied by a runtime expression. */
function hasExpressionBackedItems() {
	const rawItems: unknown = getProps().items;

	return typeof rawItems === 'string' && rawItems.startsWith(blockExpressionMarker);
}

watch(() => items, (items) => {
	if (!blockInEditor || isPreview || hasExpressionBackedItems()) {
		return;
	}

	const result = normalizeTabsBlockItems(items);
	removeStaleSlotChildren(result.items);

	if (result.changed) {
		setProp('items', result.items);
	}
}, {
	deep: true,
	immediate: true,
});

const tabsBlockItems = computed(() => normalizeTabsBlockItems(items).items);

const normalizedItems = computed<NormalizedTabsBlockItem[]>(() => tabsBlockItems.value.map((item) => {
	return {
		...item,
		label: translateString(item.label || item.value),
		slot: item.slotId,
	};
}));
const resolvedDefaultValue = computed(() => defaultValue ?? normalizedItems.value[0]?.value);
</script>

<template>
	<DTabs
		v-model="model"
		:items="normalizedItems"
		:default-value="resolvedDefaultValue"
		:color
		:variant
		:size
		:orientation
		:collapsible
		content
	>
		<template
			v-for="item of normalizedItems"
			:key="item.slot"
			#[item.slot]
		>
			<slot :name="item.slot" />
		</template>
	</DTabs>
</template>
